import React, { useState, useRef, useEffect } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';

import Dialog from '@mui/material/Dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

import DialogContent from '@mui/material/DialogContent';
import toast from 'react-hot-toast';

import Typography from '@mui/material/Typography';

export default function EditImage({
  previewImage: initialPreviewImage,
  setPreviewImage,
  open,
  isLoading,
  handleClose,
  setImageChangeData,
  updateImageMutation,
  userId,
}) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setLocalPreviewImage] = useState(initialPreviewImage || '');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef(null);

  // Update local preview when prop changes or dialog opens
  useEffect(() => {
    if (open) {
      setLocalPreviewImage(initialPreviewImage || '');
      setSelectedImage(null);
      // Check if image is already loaded (cached images)
      if (initialPreviewImage) {
        const img = new Image();
        img.onload = () => {
          setImageLoaded(true);
          setImageError(false);
        };
        img.onerror = () => {
          setImageLoaded(false);
          setImageError(true);
        };
        img.src = `${initialPreviewImage}?${Date.now()}`;
      } else {
        setImageLoaded(false);
        setImageError(false);
      }
    }
  }, [open, initialPreviewImage]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedImage(null);
      setImageLoaded(false);
      setImageError(false);
    }
  }, [open]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoaded(false);
    setImageError(true);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size should not exceed 10MB');
        return;
      }
      setSelectedImage(file);
      const newPreview = URL.createObjectURL(file);
      setLocalPreviewImage(newPreview);
      if (setPreviewImage) {
        setPreviewImage(newPreview);
      }
      setImageLoaded(false);
    }
  };

  const handleSave = () => {
    if (!selectedImage) {
      toast.error('Please select an image to upload');
      return;
    }

    if (selectedImage.size > 10 * 1024 * 1024) {
      toast.error('Image size exceeds the limit (10MB).');
      return;
    }

    const formData = new FormData();
      formData.append('image', selectedImage);
    
    // Call mutation with the data
    updateImageMutation.mutate({ 
      userId, 
      formData 
    });
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          '& .MuiPaper-root': {
            width: { xs: '350px', sm: '649px' },
            height: 'max-content',
            borderRadius: '20PX',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '20px' }}>
          <Typography variant="h4" sx={{ color: 'text.titles' }}>
            Edit Image
          </Typography>
          <CloseIcon sx={{ cursor: 'pointer' }} onClick={handleClose} />
        </Box>
          <DialogContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                cursor: 'pointer',
                '&:hover .avatar-overlay': {
                  opacity: 1,
                  pointerEvents: 'auto',
                },
              }}
              onClick={handleImageClick}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/svg+xml"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              
              {!imageLoaded && previewImage && !imageError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 200,
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.neutral',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              )}

              <Avatar
                src={previewImage ? `${previewImage}?${Date.now()}` : undefined}
                alt="Profile"
                sx={{
                  width: 200,
                  height: 200,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  fontSize: '4rem',
                  bgcolor: previewImage ? 'transparent' : 'primary.main',
                  color: 'white',
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
              >
                {!previewImage && !imageError && '?'}
              </Avatar>

              {previewImage && imageLoaded && (
                <Box
                  className="avatar-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s ease-in-out',
                    pointerEvents: 'none',
                    zIndex: 2,
                  }}
                >
                  <Tooltip title="Change Image" arrow>
                    <IconButton
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        color: 'primary.main',
                        '&:hover': {
                          bgcolor: 'background.paper',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <CameraAltIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {!previewImage && !imageError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.neutral',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    zIndex: 1,
                  }}
                >
                  <CameraAltIcon sx={{ fontSize: 48, mb: 1, color: 'text.secondary', opacity: 0.5 }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Click to upload
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {selectedImage && selectedImage.size > 10 * 1024 * 1024 && (
            <Typography
              variant="body2"
              sx={{
                color: 'error.main',
                textAlign: 'center',
                mt: 1,
              }}
            >
              Image size exceeds 10MB limit
            </Typography>
          )}
          </DialogContent>

          <Box sx={{ textAlign: 'right', pr: '31px', pb: '37px' }}>
            <LoadingButton
              loading={isLoading}
              variant="contained"
            color="main"
            onClick={handleSave}
            disabled={!selectedImage}
              sx={{ width: '164px', height: '48px', mr: '4px' }}
            >
              Save
            </LoadingButton>
            <Button
              variant="outlined"
            color="secondary"
              sx={{ width: '164px', height: '48px' }}
              onClick={() => handleClose()}
            >
              Cancel
            </Button>
          </Box>
      </Dialog>
    </div>
  );
}
